import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Users, 
  UserCheck, 
  Shield, 
  Building2, 
  ChevronDown, 
  ChevronRight,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  GitBranch
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function TeamStructureTab({ user }) {
  const [teamData, setTeamData] = useState({
    admins: [],
    qcTeam: [],
    verificationTeam: [],
    vendors: [],
    fieldAgents: []
  });
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    admins: true,
    qcTeam: true,
    verificationTeam: true,
    vendors: true,
    fieldAgents: false
  });

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      const [usersRes, vendorsRes, agentsRes] = await Promise.all([
        axios.get('/users'),
        axios.get('/vendors').catch(() => ({ data: [] })),
        axios.get('/field-agents').catch(() => ({ data: [] }))
      ]);

      const users = usersRes.data || [];
      
      setTeamData({
        admins: users.filter(u => u.role === 'agency_admin'),
        qcTeam: users.filter(u => u.role === 'qc_operations'),
        verificationTeam: users.filter(u => u.role === 'verification_team'),
        vendors: vendorsRes.data || [],
        fieldAgents: agentsRes.data || []
      });
    } catch (error) {
      console.error('Failed to load team data:', error);
      toast.error('Failed to load team structure');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const TeamMemberCard = ({ member, role }) => {
    const roleColors = {
      agency_admin: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      qc_operations: 'bg-purple-100 text-purple-700 border-purple-200',
      verification_team: 'bg-blue-100 text-blue-700 border-blue-200',
      vendor: 'bg-amber-100 text-amber-700 border-amber-200',
      field_agent: 'bg-emerald-100 text-emerald-700 border-emerald-200'
    };

    return (
      <div className={`p-4 rounded-lg border ${roleColors[role] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-semibold">{member.name || 'Unnamed'}</p>
            <div className="flex items-center gap-1 text-xs mt-1 opacity-75">
              <Mail className="w-3 h-3" />
              {member.email || '-'}
            </div>
            {member.phone && (
              <div className="flex items-center gap-1 text-xs mt-1 opacity-75">
                <Phone className="w-3 h-3" />
                {member.phone}
              </div>
            )}
            {member.location && (
              <div className="flex items-center gap-1 text-xs mt-1 opacity-75">
                <MapPin className="w-3 h-3" />
                {member.location}
              </div>
            )}
          </div>
          <Badge variant="outline" className="text-xs">
            {member.is_active !== false ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      </div>
    );
  };

  const TeamSection = ({ title, icon: Icon, members, role, color, sectionKey }) => {
    const isExpanded = expandedSections[sectionKey];
    
    return (
      <Card className="border-slate-200 shadow-sm">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => toggleSection(sectionKey)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <div className={`p-2 rounded-lg ${color}`}>
                <Icon className="w-5 h-5" />
              </div>
              {title}
              <Badge className="ml-2 bg-slate-200 text-slate-700">{members.length}</Badge>
            </CardTitle>
            {isExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
          </div>
        </CardHeader>
        {isExpanded && (
          <CardContent>
            {members.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {members.map((member, idx) => (
                  <TeamMemberCard key={member.id || idx} member={member} role={role} />
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-4">No members in this team</p>
            )}
          </CardContent>
        )}
      </Card>
    );
  };

  if (loading) {
    return <div className="p-8 text-center">Loading team structure...</div>;
  }

  const totalMembers = teamData.admins.length + teamData.qcTeam.length + 
    teamData.verificationTeam.length + teamData.vendors.length + teamData.fieldAgents.length;

  return (
    <div data-testid="team-structure-tab">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Team Structure</h1>
          <p className="text-slate-600">Organization hierarchy and team members</p>
        </div>
        <Badge className="bg-slate-900 text-white px-4 py-2 text-lg">
          {totalMembers} Total Members
        </Badge>
      </div>

      {/* Organization Hierarchy Visual */}
      <Card className="mb-6 border-slate-200 shadow-sm bg-gradient-to-br from-slate-50 to-white">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center">
            {/* Top Level - Agency Admin */}
            <div className="flex flex-col items-center mb-4">
              <div className="p-4 bg-indigo-600 rounded-full mb-2 shadow-lg">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <p className="font-bold text-lg text-indigo-900">Agency Admin</p>
              <p className="text-sm text-slate-500">{teamData.admins.length} member(s)</p>
            </div>

            {/* Connecting Line */}
            <div className="w-px h-8 bg-slate-300"></div>
            <div className="w-96 h-px bg-slate-300"></div>

            {/* Second Level - Teams */}
            <div className="grid grid-cols-4 gap-8 mt-4">
              <div className="flex flex-col items-center">
                <div className="w-px h-6 bg-slate-300 mb-2"></div>
                <div className="p-3 bg-purple-500 rounded-full mb-2 shadow">
                  <UserCheck className="w-6 h-6 text-white" />
                </div>
                <p className="font-semibold text-purple-700">QC Team</p>
                <p className="text-2xl font-bold text-purple-800">{teamData.qcTeam.length}</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-px h-6 bg-slate-300 mb-2"></div>
                <div className="p-3 bg-blue-500 rounded-full mb-2 shadow">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <p className="font-semibold text-blue-700">Verification</p>
                <p className="text-2xl font-bold text-blue-800">{teamData.verificationTeam.length}</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-px h-6 bg-slate-300 mb-2"></div>
                <div className="p-3 bg-amber-500 rounded-full mb-2 shadow">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <p className="font-semibold text-amber-700">Vendors</p>
                <p className="text-2xl font-bold text-amber-800">{teamData.vendors.length}</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-px h-6 bg-slate-300 mb-2"></div>
                <div className="p-3 bg-emerald-500 rounded-full mb-2 shadow">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <p className="font-semibold text-emerald-700">Field Agents</p>
                <p className="text-2xl font-bold text-emerald-800">{teamData.fieldAgents.length}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Team Details */}
      <div className="space-y-4">
        <TeamSection 
          title="Agency Administrators" 
          icon={Shield} 
          members={teamData.admins} 
          role="agency_admin"
          color="bg-indigo-100 text-indigo-600"
          sectionKey="admins"
        />
        
        <TeamSection 
          title="QC & Approval Team" 
          icon={UserCheck} 
          members={teamData.qcTeam} 
          role="qc_operations"
          color="bg-purple-100 text-purple-600"
          sectionKey="qcTeam"
        />
        
        <TeamSection 
          title="Verification Team" 
          icon={Users} 
          members={teamData.verificationTeam} 
          role="verification_team"
          color="bg-blue-100 text-blue-600"
          sectionKey="verificationTeam"
        />
        
        <TeamSection 
          title="Vendors" 
          icon={Building2} 
          members={teamData.vendors} 
          role="vendor"
          color="bg-amber-100 text-amber-600"
          sectionKey="vendors"
        />
        
        <TeamSection 
          title="Field Agents" 
          icon={Briefcase} 
          members={teamData.fieldAgents} 
          role="field_agent"
          color="bg-emerald-100 text-emerald-600"
          sectionKey="fieldAgents"
        />
      </div>
    </div>
  );
}
